<script setup lang="ts">
import { onBeforeMount, onBeforeUnmount, ref, onMounted, watch } from "vue";
import { environmentData } from "./helper/environment";
import { useGlobalStore } from "./store/global";
import siteCoreMockData from "./mock-data/siteCoreData.json";
import { ignLogoutRedirect, initializeIamViaIgn } from "@/utils/authentication-helpers";
import { setSessionUser, deviceInfo } from "@/utils/analytics";
import { checkDeviceType, getJwtToken } from "@/utils/common";
import { decodeJwtBody } from "@maersk-global/shared-js";
import { useLDReady, useLDFlag } from "launchdarkly-vue-client-sdk";
import { useTnTRum } from "./store/tntRum";

import "@maersk-global/mds-components-core/mc-modal";
import "@maersk-global/mds-components-core/mc-button";
import { replacePlaceholder } from "@/utils/string_formatter";

const terminalScopesFlag = useLDFlag("apmt-tnt-terminals", {});
const loginRequiredTerminals = useLDFlag("apmt-tracktrace-login-terminals", {
  terminalList: [],
  enabled: (boolean: any) => boolean
});

const tasTerminals = useLDFlag("apmt-tnt-tas-terminals", {
  terminalList: [],
  enabled: (boolean: any) => boolean
});

const globalStore = useGlobalStore();
const isScLoaded = ref(false);
const ldReady = useLDReady();
const tntRum = useTnTRum();
const sessionWarningVisible = ref(false);
const sessionCountdown = ref(30);
const isRefreshingToken = ref(false);

const AUTO_REFRESH_WINDOW_MS = 5 * 60 * 1000;
const USER_ACTIVITY_WINDOW_MS = 5 * 60 * 1000;
const WARNING_WINDOW_MS = 30 * 1000;
let sessionMonitorInterval: ReturnType<typeof setInterval> | null = null;
let lastLoggedExpiryMs: number | null = null;
let lastAutoRefreshAttemptExpiryMs: number | null = null;
let lastUserActivityAt = Date.now();

const labels = {
  sessionExpiringSoon: "",
  sessionExpiringSoonMessage: "",
  logout: "",
  extendSession: ""
};

const updateLabels = () => {
  const common = globalStore.sitecoreData?.common || {};
  const cta = globalStore.sitecoreData?.cta || {};

  labels.sessionExpiringSoon = common.session_expiring_soon || "";
  labels.sessionExpiringSoonMessage = common.session_expiring_soon_message || "";
  labels.logout = cta.logout || "";
  labels.extendSession = cta.extend_session || "";
};

const getFormattedTime = (timestamp: number): string => {
  return new Date(timestamp).toISOString();
};

onBeforeMount(async () => {
  localStorage.setItem("terminal", "Global");
  localStorage.setItem("analytics_appName", "track-and-trace");
  document.addEventListener("IGN_Init", () => {
    initializeIamViaIgn();
  });
  const headers = new Headers();
  headers.append("Accept", "application/json");
  headers.append("Consumer-Key", environmentData.siteCoreApiConsumerKey);

  const fetchErrorPhrases = async () => {
    try {
      const url = environmentData.siteCoreApiUrl.replace("{lang}", localStorage.getItem("language") || "en");
      const response = await fetch(url, {
        method: "GET",
        headers: headers
      });

      if (!response.ok || response.status !== 200) {
        throw new Error("Network response was not ok");
      }
      const jsonResponse = await response.json();
      globalStore.updateSitecoreData(jsonResponse);
      isScLoaded.value = true;
    } catch (error) {
      isScLoaded.value = true;
      globalStore.updateSitecoreData(siteCoreMockData); // Fallback to hardcoded data if fetch fails
    }
    updateLabels();
  };
  await fetchErrorPhrases();
});

onMounted(() => {
  localStorage.setItem("terminal", "Global");
  localStorage.setItem("analytics_appName", "track-and-trace");
  const deviceType = checkDeviceType() || "";
  tntRum.logRumEvent("fetch", "App", "Device type", deviceType);
  deviceInfo(deviceType);
  updateUserData();
  const lang = localStorage.getItem("language");
  document.documentElement.setAttribute("dir", lang === "ar" ? "rtl" : "ltr");
  const mdsElement = document.querySelector(".mds");
  if (mdsElement) {
    mdsElement.classList.toggle("rtl", lang === "ar");
  }

  registerActivityListeners();
  startSessionMonitor();
});

onBeforeUnmount(() => {
  clearSessionMonitor();
  unregisterActivityListeners();
});

const markUserActivity = () => {
  lastUserActivityAt = Date.now();
};

const activityEvents: Array<keyof WindowEventMap> = ["click", "keydown", "mousemove", "scroll", "touchstart"];

const registerActivityListeners = () => {
  for (const eventName of activityEvents) {
    window.addEventListener(eventName, markUserActivity, { passive: true });
  }
};

const unregisterActivityListeners = () => {
  for (const eventName of activityEvents) {
    window.removeEventListener(eventName, markUserActivity);
  }
};

const isUserActiveOnApp = (): boolean => {
  return document.visibilityState === "visible" && Date.now() - lastUserActivityAt <= USER_ACTIVITY_WINDOW_MS;
};

const getAccessTokenExpiryMs = (): number | null => {
  const jwt = getJwtToken("access_token");
  if (!jwt) {
    return null;
  }

  const decodedJwt: any = decodeJwtBody(jwt);
  if (decodedJwt?.error || !decodedJwt?.exp) {
    return null;
  }

  return Number(decodedJwt.exp) * 1000;
};

const updateSessionWarningState = () => {
  // Skip update if token refresh is in progress to avoid modal flashing
  if (isRefreshingToken.value) {
    return;
  }

  const expiresAtMs = getAccessTokenExpiryMs();

  if (!expiresAtMs) {
    if (lastLoggedExpiryMs !== null) {
      console.info("[Session Debug] access token not found or invalid");
    }
    lastLoggedExpiryMs = null;
    sessionWarningVisible.value = false;
    return;
  }

  if (lastLoggedExpiryMs !== expiresAtMs) {
    lastLoggedExpiryMs = expiresAtMs;
  }

  const remainingMs = expiresAtMs - Date.now();

  if (remainingMs <= 0) {
    console.info(
      `[Session Debug] Token expired at=${getFormattedTime(expiresAtMs)} | logoutTriggeredAt=${getFormattedTime(
        Date.now()
      )}`
    );
    sessionWarningVisible.value = false;
    clearSessionMonitor();
    ignLogoutRedirect();
    return;
  }

  if (remainingMs <= AUTO_REFRESH_WINDOW_MS && isUserActiveOnApp() && lastAutoRefreshAttemptExpiryMs !== expiresAtMs) {
    lastAutoRefreshAttemptExpiryMs = expiresAtMs;
    extendSession(true);
    return;
  }

  if (remainingMs <= WARNING_WINDOW_MS) {
    sessionWarningVisible.value = true;
    sessionCountdown.value = Math.max(0, Math.ceil(remainingMs / 1000));
    return;
  }

  sessionWarningVisible.value = false;
};

const clearSessionMonitor = () => {
  if (sessionMonitorInterval) {
    clearInterval(sessionMonitorInterval);
    sessionMonitorInterval = null;
  }
};

const startSessionMonitor = () => {
  clearSessionMonitor();
  updateSessionWarningState();
  sessionMonitorInterval = setInterval(() => {
    updateSessionWarningState();
  }, 1000);
};

const extendSession = (triggeredByActivity = false) => {
  sessionWarningVisible.value = false;
  if (!triggeredByActivity) {
    sessionCountdown.value = 60;
  }
  isRefreshingToken.value = true;

  const oldExpiry = getAccessTokenExpiryMs();

  const refreshTokenFn = (window as any).IGN_Refresh_Token;
  if (typeof refreshTokenFn === "function") {
    refreshTokenFn();

    // Wait for IAM to update localStorage with new token
    let attempts = 0;
    const maxAttempts = 50; // max ~5 seconds
    const checkForNewToken = setInterval(() => {
      attempts++;
      const newExpiry = getAccessTokenExpiryMs();

      // Token was refreshed (expiry changed) or max attempts reached
      if ((oldExpiry && newExpiry && newExpiry > oldExpiry) || attempts >= maxAttempts) {
        clearInterval(checkForNewToken);
        if (oldExpiry && newExpiry) {
          const extensionSeconds = Math.floor((newExpiry - oldExpiry) / 1000);
          console.info(`[Session Debug] Token extensionSeconds=${extensionSeconds}`);
        }
        // Resume session monitoring after token is refreshed
        isRefreshingToken.value = false;
        startSessionMonitor();
      }
    }, 100); // Check every 100ms
    return;
  }

  console.warn("[Session Debug] window.IGN_Refresh_Token is not available");
  isRefreshingToken.value = false;
};

const logoutSession = () => {
  sessionWarningVisible.value = false;
  clearSessionMonitor();
  ignLogoutRedirect();
};

const updateUserData = () => {
  const decodedToken = decodeJwtBody(localStorage.getItem(environmentData.accessTokenLink));
  if (decodedToken) {
    const userData = {
      email: decodedToken?.sub,
      id: decodedToken?.userUuid,
      username: decodedToken?.subname,
      attributes: {
        scopes: JSON.stringify(decodedToken?.scope)
      }
    };
    globalStore.updateUserEmail(userData.email);
    setSessionUser(userData);
    tntRum.faroRum?.api.setUser(userData);
  }
};

watch(
  terminalScopesFlag,
  async (newVal: any) => {
    globalStore.updateAllowedTerminals(newVal?.terminals || []);
  },
  { immediate: true }
);

watch(
  loginRequiredTerminals,
  async (newVal: any) => {
    globalStore.updateLoginRequiredTerminals(newVal || []);
  },
  { immediate: true }
);

watch(
  tasTerminals,
  async (newVal: any) => {
    globalStore.updateTASTerminals(newVal || []);
  },
  { immediate: true }
);
</script>

<template>
  <!-- Router configuration for the main widget -->

  <div v-if="isScLoaded && ldReady">
    <router-view />

    <div v-if="globalStore.sitecoreData">
      <mc-modal
        :heading="
          replacePlaceholder(labels.sessionExpiringSoon, {
            sessionCountdown: sessionCountdown.toString()
          })
        "
        dimension="small"
        :open="sessionWarningVisible"
        hiddenclose
        escapecloseactiondisabled
        backdropcloseactiondisabled
      >
        <p>
          {{ replacePlaceholder(labels.sessionExpiringSoonMessage, { sessionCountdown: sessionCountdown.toString() }) }}
        </p>
        <mc-button
          slot="secondaryAction"
          appearance="neutral"
          variant="outlined"
          dialogaction="cancel"
          :label="labels.logout"
          @click="logoutSession"
        ></mc-button>
        <mc-button
          id="confirm"
          slot="primaryAction"
          appearance="primary"
          :label="labels.extendSession"
          @click="extendSession"
        ></mc-button>
      </mc-modal>
    </div>
  </div>
</template>

<style>
[dir="rtl"] div {
  direction: rtl;
  text-align: right;
}
.mds.rtl * {
  direction: rtl;
  text-align: right;
}

.session-warning-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.45);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 9999;
  padding: 1rem;
}

.session-warning-modal {
  background: #ffffff;
  width: 100%;
  max-width: 440px;
  border-radius: 8px;
  box-shadow: 0 20px 40px rgba(0, 0, 0, 0.2);
  padding: 1.25rem;
}

.session-warning-title {
  margin: 0;
  font-size: 1.125rem;
  line-height: 1.4;
}

.session-warning-description {
  margin: 0.75rem 0 0;
  font-size: 0.95rem;
}

.session-warning-actions {
  display: flex;
  justify-content: flex-end;
  gap: 0.75rem;
  margin-top: 1.25rem;
}

.session-btn {
  border: 0;
  border-radius: 6px;
  padding: 0.55rem 0.95rem;
  cursor: pointer;
  font-weight: 600;
}

.session-btn-secondary {
  background: #e5e5e5;
  color: #222222;
}

.session-btn-primary {
  background: #003366;
  color: #ffffff;
}
</style>
